import React, { useState } from "react";
import axios from "axios";
import { Plane, Plus } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../../contexts/AuthContext";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA  = { fontFamily: "'Lora', Georgia, serif" };

const SEGMENTS = [
  { value: "aller_1",  label: "Aller (1er vol)" },
  { value: "aller_2",  label: "Correspondance aller" },
  { value: "retour_1", label: "Vol retour" },
  { value: "retour_2", label: "Correspondance retour" },
];

export function AddFlightForm({ tripId, defaultDate, onAdded, disabled }) {
  const [flightNumber, setFlightNumber] = useState("");
  const [flightDate,   setFlightDate]   = useState(defaultDate || "");
  const [segment,      setSegment]      = useState("aller_1");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!flightNumber.trim()) { setError("Entrez un numéro de vol"); return; }
    if (!flightDate)          { setError("Sélectionnez une date");   return; }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/trips/${tripId}/flights`, {
        flight_number: flightNumber.trim().toUpperCase().replace(/\s/g, ""),
        flight_date:   flightDate,
        segment,
      });
      onAdded?.(data);
      setFlightNumber("");
      toast.success(`Vol ${data.flight_number} ajouté`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'ajout";
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="card-surface space-y-3"
      data-testid="add-flight-form"
    >
      <h3 className="text-xl flex items-center gap-2" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
        <Plane size={15} strokeWidth={1.5} className="text-[#C17F59]" />
        Ajouter un vol à suivre
      </h3>

      <div className="grid grid-cols-2 gap-3">
        {/* Numéro de vol */}
        <div className="col-span-2 sm:col-span-1">
          <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={LORA}>
            Numéro de vol
          </label>
          <input
            type="text"
            value={flightNumber}
            onChange={(e) => setFlightNumber(e.target.value.toUpperCase().replace(/\s/g, ""))}
            placeholder="Ex: AF642, AFL321, FR1234"
            className="w-full px-3 py-2 border border-[#DCD4C6] bg-[#F9F6F0] text-[#2B2623] text-sm focus:outline-none focus:border-[#C17F59] rounded-sm"
            style={LORA}
            disabled={loading || disabled}
            maxLength={8}
            data-testid="flight-number-input"
          />
        </div>

        {/* Date */}
        <div className="col-span-2 sm:col-span-1">
          <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={LORA}>
            Date du vol
          </label>
          <input
            type="date"
            value={flightDate}
            onChange={(e) => setFlightDate(e.target.value)}
            className="w-full px-3 py-2 border border-[#DCD4C6] bg-[#F9F6F0] text-[#2B2623] text-sm focus:outline-none focus:border-[#C17F59] rounded-sm"
            style={LORA}
            disabled={loading || disabled}
            data-testid="flight-date-input"
          />
        </div>
      </div>

      {/* Segment */}
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={LORA}>
          Type de vol
        </label>
        <select
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          className="w-full px-3 py-2 border border-[#DCD4C6] bg-[#F9F6F0] text-[#2B2623] text-sm focus:outline-none focus:border-[#C17F59] rounded-sm"
          style={LORA}
          disabled={loading || disabled}
          data-testid="flight-segment-select"
        >
          {SEGMENTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 italic" style={LORA} data-testid="flight-form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || disabled}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#2B2623] text-[#F9F6F0] text-sm hover:bg-[#C17F59] disabled:opacity-50 transition-colors rounded-sm"
        style={LORA}
        data-testid="add-flight-submit"
      >
        {loading ? (
          <><div className="w-4 h-4 border-2 border-[#F9F6F0] border-t-transparent rounded-full animate-spin" /> Vérification en cours…</>
        ) : (
          <><Plus size={14} strokeWidth={2} /> Ajouter ce vol</>
        )}
      </button>

      {disabled && (
        <p className="text-xs text-[#9A8F86] text-center italic" style={LORA}>
          Maximum 4 vols atteint
        </p>
      )}
    </form>
  );
}
